import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import taskService from '../../services/taskService';

const TaskList = ({ 
  showMyTasksOnly = false, 
  showFilters = true, 
  compact = false,
  onTaskUpdate = null,
  initialFilters = {}
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    task_type: '',
    category: '',
    search: '',
    overdue_only: false,
    ...initialFilters
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: compact ? 10 : 20,
    total: 0,
    totalPages: 0
  });
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(new Set());

  // Ensure tasks is always an array
  useEffect(() => {
    if (!Array.isArray(tasks)) {
      console.warn('[TaskList] Tasks is not an array, resetting to empty array');
      setTasks([]);
    }
  }, [tasks]);

  // Add comprehensive error boundary
  const [componentError, setComponentError] = useState(null);
  
  useEffect(() => {
    const handleError = (error) => {
      console.error('[TaskList] Component error:', error);
      setComponentError(error.message);
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (componentError) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Component Error</h3>
        <p className="text-muted-foreground mb-4">{componentError}</p>
        <Button onClick={() => {
          setComponentError(null);
          window.location.reload();
        }}>Reload</Button>
      </div>
    );
  }

  // Load tasks
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = taskService.buildQueryParams(
        filters, 
        pagination.page, 
        pagination.pageSize
      );

      const response = showMyTasksOnly 
        ? await taskService.getMyTasks(params)
        : await taskService.getTasks(params);

      console.log('[TaskList] API Response:', response);
      
      // Handle different response structures
      const tasksData = response?.tasks || response?.data?.tasks || [];
      const totalCount = response?.total || response?.data?.total || 0;
      const totalPages = response?.total_pages || response?.data?.total_pages || Math.ceil(totalCount / pagination.pageSize);
      
      console.log('[TaskList] Tasks data:', tasksData);
      
      // Ensure tasksData is an array before mapping
      if (Array.isArray(tasksData)) {
        const formattedTasks = tasksData.map(task => {
          try {
            return taskService.formatTask(task);
          } catch (formatError) {
            console.warn('[TaskList] Error formatting task:', task, formatError);
            return task; // Return unformatted task as fallback
          }
        });
        setTasks(formattedTasks);
      } else {
        console.warn('[TaskList] Tasks data is not an array:', tasksData);
        setTasks([]);
      }
      setPagination(prev => ({
        ...prev,
        total: totalCount,
        totalPages: totalPages
      }));
    } catch (err) {
      setError(err.message);
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, showMyTasksOnly]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle task action
  const handleTaskAction = async (taskId, action, notes = '', actualDuration = null) => {
    try {
      setActionLoading(prev => new Set(prev).add(taskId));
      
      const actionData = {
        action,
        notes: notes || undefined,
        actual_duration: actualDuration || undefined
      };

      const response = await taskService.performTaskAction(taskId, actionData);
      
      // Update task in list
      setTasks(prev => (Array.isArray(prev) ? prev : []).map(task => 
        task.id === taskId 
          ? taskService.formatTask(response.task)
          : task
      ));

      if (onTaskUpdate) {
        onTaskUpdate(response.task);
      }

      // Reload to get updated data
      loadTasks();
    } catch (err) {
      setError(err.message);
      console.error('Error performing task action:', err);
    } finally {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedTasks.size === 0) return;

    try {
      setLoading(true);
      const promises = Array.from(selectedTasks).map(taskId =>
        taskService.performTaskAction(taskId, { action })
      );
      
      await Promise.all(promises);
      setSelectedTasks(new Set());
      loadTasks();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle task selection
  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Get priority icon and color
  const getPriorityConfig = (priority) => {
    const configs = {
      urgent: { icon: 'AlertTriangle', color: 'text-red-500' },
      high: { icon: 'ArrowUp', color: 'text-orange-500' },
      medium: { icon: 'Minus', color: 'text-yellow-500' },
      low: { icon: 'ArrowDown', color: 'text-green-500' }
    };
    return configs[priority] || configs.medium;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      on_hold: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors.pending;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
    if (diffDays > 1) return `In ${diffDays} days`;
    return date.toLocaleDateString();
  };

  if (loading && (Array.isArray(tasks) ? tasks.length === 0 : true)) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading tasks...</span>
      </div>
    );
  }

  // Ensure tasks is an array before rendering
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      {showFilters && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Statuses</option>
                {(() => {
                  try {
                    const statuses = taskService.getTaskStatuses();
                    return (Array.isArray(statuses) ? statuses : []).map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ));
                  } catch (error) {
                    console.error('[TaskList] Error getting task statuses:', error);
                    return null;
                  }
                })()}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Priorities</option>
                {(() => {
                  try {
                    const priorities = taskService.getPriorityLevels();
                    return (Array.isArray(priorities) ? priorities : []).map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ));
                  } catch (error) {
                    console.error('[TaskList] Error getting priority levels:', error);
                    return null;
                  }
                })()}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Type
              </label>
              <select
                value={filters.task_type}
                onChange={(e) => handleFilterChange('task_type', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Types</option>
                {(() => {
                  try {
                    const types = taskService.getTaskTypes();
                    return (Array.isArray(types) ? types : []).map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ));
                  } catch (error) {
                    console.error('[TaskList] Error getting task types:', error);
                    return null;
                  }
                })()}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                {(() => {
                  try {
                    const categories = taskService.getTaskCategories();
                    return (Array.isArray(categories) ? categories : []).map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ));
                  } catch (error) {
                    console.error('[TaskList] Error getting task categories:', error);
                    return null;
                  }
                })()}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={filters.overdue_only}
                  onChange={(e) => handleFilterChange('overdue_only', e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">Overdue only</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilters({
                    status: '',
                    priority: '',
                    task_type: '',
                    category: '',
                    search: '',
                    overdue_only: false
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedTasks.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedTasks.size} task(s) selected
            </span>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('complete')}
                disabled={loading}
              >
                Mark Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('cancel')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTasks(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
            <div>
              <h3 className="text-red-800 font-medium">Error</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="space-y-3">
        {safeTasks.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ListTodo" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No tasks found</h3>
            <p className="text-muted-foreground">
              {Object.values(filters).some(v => v) 
                ? "Try adjusting your filters to see more tasks."
                : "There are no tasks to display."
              }
            </p>
          </div>
        ) : (
          safeTasks.map(task => (
            <div
              key={task.id}
              className={`bg-card border rounded-lg p-4 hover:shadow-md transition-shadow ${
                task.isOverdue ? 'border-red-200 bg-red-50' : 'border-border'
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Selection checkbox */}
                <input
                  type="checkbox"
                  checked={selectedTasks.has(task.id)}
                  onChange={() => toggleTaskSelection(task.id)}
                  className="mt-1 rounded border-border"
                />

                {/* Task content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground mb-1">
                        {task.title}
                        {task.isOverdue && (
                          <Icon name="AlertTriangle" size={16} className="inline ml-2 text-red-500" />
                        )}
                      </h4>
                      {task.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {task.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {/* Priority */}
                        <div className={`flex items-center space-x-1 ${getPriorityConfig(task.priority).color}`}>
                          <Icon name={getPriorityConfig(task.priority).icon} size={12} />
                          <span>{task.formattedPriority}</span>
                        </div>
                        
                        {/* Status */}
                        <span className={`px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                          {task.formattedStatus}
                        </span>
                        
                        {/* Type */}
                        <span className="text-muted-foreground">
                          {task.formattedType}
                        </span>
                        
                        {/* Category */}
                        <span className="text-muted-foreground">
                          {task.formattedCategory}
                        </span>
                        
                        {/* Due date */}
                        {task.due_date && (
                          <span className={`${task.isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                            Due: {formatDate(task.due_date)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Task actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {task.canStart && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTaskAction(task.id, 'start')}
                          disabled={actionLoading.has(task.id)}
                        >
                          {actionLoading.has(task.id) ? (
                            <Icon name="Loader2" size={14} className="animate-spin" />
                          ) : (
                            <>
                              <Icon name="Play" size={14} className="mr-1" />
                              Start
                            </>
                          )}
                        </Button>
                      )}
                      
                      {task.canComplete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTaskAction(task.id, 'complete')}
                          disabled={actionLoading.has(task.id)}
                        >
                          {actionLoading.has(task.id) ? (
                            <Icon name="Loader2" size={14} className="animate-spin" />
                          ) : (
                            <>
                              <Icon name="CheckCircle" size={14} className="mr-1" />
                              Complete
                            </>
                          )}
                        </Button>
                      )}
                      
                      {task.canHold && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTaskAction(task.id, 'hold')}
                          disabled={actionLoading.has(task.id)}
                        >
                          <Icon name="Pause" size={14} />
                        </Button>
                      )}
                      
                      {task.canResume && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTaskAction(task.id, 'resume')}
                          disabled={actionLoading.has(task.id)}
                        >
                          <Icon name="Play" size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total} tasks
          </p>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page <= 1}
            >
              Previous
            </Button>
            
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;